import { useState, useRef, useEffect, useCallback } from 'react';
import { ActionIcon, Button, Paper, Stack, Text } from '@cowbell/ui/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheck,
  faExpand,
  faSidebar,
  faTableColumns,
  faWindowRestore,
} from '@fortawesome/pro-regular-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { useShellLayout } from '../../provider';
import type { LayoutMode } from '../../state/shell-state';

export interface LayoutSwitcherProps {
  readonly className?: string;
  /** Size of the trigger ActionIcon. Defaults to 'sm'. */
  readonly size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
}

const MODES: Array<{
  readonly mode: LayoutMode;
  readonly label: string;
  readonly icon: IconDefinition;
}> = [
  { mode: 'floating', label: 'Floating', icon: faWindowRestore },
  { mode: 'sidebar', label: 'Sidebar', icon: faSidebar },
  { mode: 'fullscreen', label: 'Full screen', icon: faExpand },
];

export function LayoutSwitcher({
  className,
  size = 'sm',
}: LayoutSwitcherProps) {
  const { mode, setMode } = useShellLayout();
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const close = useCallback(() => setIsOpen(false), []);

  useEffect(() => {
    if (!isOpen || !triggerRef.current || !dropdownRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    dropdownRef.current.style.top = `${rect.bottom + 4}px`;
    dropdownRef.current.style.right = `${globalThis.innerWidth - rect.right}px`;
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopImmediatePropagation();
        close();
      }
    };
    document.addEventListener('keydown', handler, true);
    return () => document.removeEventListener('keydown', handler, true);
  }, [isOpen, close]);

  return (
    <>
      <ActionIcon
        ref={triggerRef}
        variant="subtle"
        size={size}
        aria-label="Switch layout"
        aria-expanded={isOpen}
        aria-haspopup="true"
        onClick={() => setIsOpen((o) => !o)}
        className={className}
      >
        <FontAwesomeIcon icon={faTableColumns} />
      </ActionIcon>
      {isOpen && (
        <>
          <div
            onClick={close}
            style={{ position: 'fixed', inset: 0, zIndex: 1099 }}
            aria-hidden="true"
          />
          <Paper
            ref={dropdownRef}
            withBorder
            shadow="md"
            style={{
              position: 'fixed',
              zIndex: 1100,
              width: 176,
              background: 'var(--mantine-color-body)',
            }}
          >
            <Text
              size="xs"
              fw={600}
              c="dimmed"
              tt="uppercase"
              px="sm"
              pt="sm"
              pb="xs"
            >
              Switch to
            </Text>
            <Stack gap={2} p="xs">
              {MODES.map(({ mode: m, label, icon }) => (
                <Button
                  key={m}
                  variant={mode === m ? 'light' : 'subtle'}
                  color={mode === m ? 'blue' : 'gray'}
                  justify="flex-start"
                  fullWidth
                  leftSection={
                    <FontAwesomeIcon
                      icon={icon}
                      style={{ width: 14, height: 14 }}
                    />
                  }
                  rightSection={
                    mode === m ? (
                      <FontAwesomeIcon
                        icon={faCheck}
                        style={{ width: 12, height: 12 }}
                      />
                    ) : null
                  }
                  onClick={() => {
                    setMode(m);
                    close();
                  }}
                >
                  {label}
                </Button>
              ))}
            </Stack>
          </Paper>
        </>
      )}
    </>
  );
}
